import { Grid, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { basePath } from '../lib/basePath';

type QuestionMarkProps = { path: string };

// Styled Component did not want to work with this img
function QuestionMarkIconComponent({ path }: QuestionMarkProps): JSX.Element {
    return <img alt="404 - page not found" src={`${path}`} />;
}

const StyledContainerGrid = styled(Grid)({
    position: 'fixed',
    padding: '1rem',
    margin: 0,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
});

const StyledTypography = styled(Typography)({
    marginBottom: '1rem',
});

export default function FourOhFour(): JSX.Element {
    const router = useRouter();
    const base = basePath(router.asPath.split('?')[0]);

    useEffect(() => {
        document.title = '404 Not Found';
    }, []);

    return (
        <>
            {/* 
                When this page is rendered on the server at build time, the router.asPath is '/404'.
                However, on the client, router.asPath is the actual URL the user is on
                ie. /this/page/does/not/exist

                Therefore, in order for the fonts, favicon and other assets to load correctly,
                we need to add them to <head> again with the correct relative path
             */}
            <Head>
                <link rel="manifest" href={`${base}/manifest.json`} />
                {/* Begin: The following settings exist in manifest.json too, so make sure you sync the changes. */}
                <link rel="icon" href={`${base}/favicon.ico`} />
                <link rel="apple-touch-icon" href={`${base}/icons/logo512.png`} />
                <link rel="mask-icon" href={`${base}/icons/website_icon.svg`} color="#25272A" />
                {/* End: manifest.json */}

                <link rel="stylesheet" href={`${base}/fonts/Urbanist/Urbanist.css`} />
                <link rel="stylesheet" href={`${base}/fonts/Poppins/Poppins.css`} />
                <link rel="stylesheet" href={`${base}/flags/flag.css`} />
            </Head>

            <StyledContainerGrid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                spacing={5}
            >
                <Grid item>
                    <QuestionMarkIconComponent path="/404_question_mark.svg" />
                </Grid>
                <Grid
                    item
                    sx={{
                        maxWidth: '40rem',
                    }}
                >
                    <Grid>
                        <StyledTypography variant="h4">Oops...</StyledTypography>
                        <StyledTypography variant="body1">
                            We can't find the page you're looking for.
                        </StyledTypography>
                        <StyledTypography variant="body1">
                            This is a 404 error, which means the page is no longer here or never
                            existed in the first place (Sorry!)
                        </StyledTypography>
                        <StyledTypography variant="body1">
                            We wanted to make you happier, but when we searched for a 404 joke, we
                            couldn't find one.
                        </StyledTypography>
                    </Grid>
                    <Grid>
                        <Button
                            data-testid="home-button"
                            variant="contained"
                            onClick={() => {
                                // Hard redirect (instead of router.push) so fonts, favicon etc are loaded correctly
                                window.location.href = `${base}/`;
                            }}
                        >
                            Country road, take me home
                        </Button>
                    </Grid>
                </Grid>
            </StyledContainerGrid>
        </>
    );
}
